import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { AlertTriangle, Bike, ExternalLink, Image, Loader2, Navigation, Phone, RefreshCw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { DetailSection } from '@/components/orders/detail-section';
import {
  deliveryErrorMessage,
  deliveryProviderDefinition,
  hasAssignedRider,
  isActiveDispatchStatus,
  localHoldLeadOf,
  supportsOf,
} from '@/lib/delivery-providers-client';
import { dispatchNeedsAttention, orderDeliveryClient, type DeliveryDispatchDto } from '@/lib/deliveries-client';
import type { Order, OrderStatus } from '@/lib/orders-client';
import { CancelDeliveryDialog } from './cancel-delivery-dialog';
import { DeliveryCodeRow } from './delivery-code';
import { localHoldUntil } from './delivery-form';
import { DeliveryStatusChip } from './delivery-status-chip';
import { deliveryStatusKey } from './delivery-status';
import { RequestRiderDialog } from './request-rider-dialog';
import { useDeliveryCapabilities } from './use-delivery-providers-query';
import { orderCoordinates } from './request-rider-dialog';

/**
 * The statuses an order can call a rider from: placed and not finished. A rider for an unpaid,
 * cancelled or completed order would be a booking nobody needs.
 */
const RIDER_REQUESTABLE_STATUSES: OrderStatus[] = [
  'pending',
  'confirmed',
  'pos_push_failed',
  'pushed_to_external_pending',
  'pushed_to_external',
];

export function orderDeliveryQueryKey(orderId: string) {
  return ['order-delivery', orderId] as const;
}

/**
 * The order detail panel's "Envío" block (spec §6): the rider booking as it stands — status and its
 * timeline, the driver with tap-to-call, prep time, tracking and proof — plus what the operator can
 * do about it: ask for a rider, cancel, re-read the provider.
 * Rendered only for delivery orders that already had a rider, or that can still ask for one (branch
 * with a provider, order still running) — a finished order shows its rider's run, never the request.
 */
export function OrderDeliverySection({ order }: { order: Order }) {
  const { t } = useTranslation('orders');
  const queryClient = useQueryClient();
  const summary = order.delivery ?? null;
  // The session-wide capabilities (aggregate-aware) say whether this branch can call a rider now.
  const capabilities = useDeliveryCapabilities(order.deliveryType === 'delivery');
  const link =
    capabilities.data?.find((capability) => capability.locationId === order.locationId && capability.enabled) ?? null;
  const requestableOrder = RIDER_REQUESTABLE_STATUSES.includes(order.status);
  const visible = order.deliveryType === 'delivery' && (!!summary || (!!link && requestableOrder));

  const [requesting, setRequesting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [busy, setBusy] = useState<'refresh' | 'cancel' | null>(null);

  const dispatchQuery = useQuery({
    queryKey: orderDeliveryQueryKey(order.id),
    queryFn: () => orderDeliveryClient.get(order.id),
    enabled: visible,
    retry: false,
  });

  // The order's summary moved (a webhook, the board feed): the full dispatch behind it did too.
  const summaryVersion = summary ? `${summary.status}|${summary.updatedAt}` : null;
  useEffect(() => {
    if (!summaryVersion) return;
    void queryClient.invalidateQueries({ queryKey: orderDeliveryQueryKey(order.id) });
  }, [summaryVersion, order.id, queryClient]);

  if (!visible) return null;

  const dispatch: DeliveryDispatchDto | null = dispatchQuery.data ?? null;
  const status = dispatch?.status ?? summary?.status ?? null;
  const provider = deliveryProviderDefinition(dispatch?.provider ?? summary?.provider ?? link?.provider);
  // The dispatch's own provider: the branch's capabilities entry when it is still that provider (the
  // API's `capabilities`), else the registry.
  const providerRef = link && link.provider === provider.id ? link : provider.id;
  const supports = supportsOf(providerRef);
  const courierLead = localHoldLeadOf(providerRef);
  const active = isActiveDispatchStatus(status);
  // The hand-over code the customer gives the rider — only a provider with `DELIVERY_CODE` means it,
  // and only while the run is on (delivered, cancelled or failed, it is of no use to anyone).
  const deliveryCode = supports.deliveryCode && active ? (dispatch?.validationCode ?? null) : null;
  // A cancellation the business made itself is an outcome, not something to look at.
  const attention = dispatchNeedsAttention(dispatch ?? (summary ? { ...summary } : null));
  // A POS order stays `pushed_to_external` after the hand-over: a delivered rider is what finished it.
  const canRequest = !!link && !active && status !== 'delivered' && requestableOrder;
  // The provider routes on coordinates: an order whose address was never placed cannot travel.
  const hasCoordinates = !!orderCoordinates(order);
  const driverName = dispatch?.driverName ?? summary?.driverName ?? null;
  const driverPhone = dispatch?.driverPhone ?? summary?.driverPhone ?? null;
  // The fleet's live map: nothing to follow until a rider has the booking.
  const trackingUrl = hasAssignedRider(status) ? (dispatch?.trackingUrl ?? summary?.trackingUrl ?? null) : null;
  const bookingNumber = dispatch?.bookingNumericId ?? summary?.bookingNumericId ?? null;
  const events = [...(dispatch?.events ?? [])].sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());

  const adopt = (next: DeliveryDispatchDto | null) => {
    queryClient.setQueryData(orderDeliveryQueryKey(order.id), next);
  };

  const run = async (
    kind: 'refresh' | 'cancel',
    action: () => Promise<DeliveryDispatchDto | null>,
    successKey?: string,
  ) => {
    setBusy(kind);
    try {
      adopt(await action());
      if (successKey) toast.success(t(successKey, { provider: provider.name }));
      return true;
    } catch (err) {
      toast.error(deliveryErrorMessage(err) ?? t('delivery.section.actionFailed'));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const handleCancel = async (reason: string) => {
    if (await run('cancel', () => orderDeliveryClient.cancel(order.id, reason), 'delivery.section.cancelDone')) {
      setCancelling(false);
    }
  };

  // A booking Menteo holds itself (a provider without `HOLD`): when it is sent — the API's `bookAfter`,
  // or prep less the courier lead on an older API.
  const courierAt = dispatch && status === 'on_hold' ? localHoldUntil(dispatch, courierLead) : null;
  // A booking the provider holds (Picker): when it starts looking for a rider on its own.
  const searchAt =
    !courierAt && dispatch && status === 'on_hold' && dispatch.prepTimeMinutes > 0
      ? new Date(new Date(dispatch.requestedAt).getTime() + dispatch.prepTimeMinutes * 60_000)
      : null;

  return (
    <DetailSection
      icon={Bike}
      title={t('delivery.section.title')}
      action={
        (summary || dispatch) && (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 gap-1.5 text-xs"
            disabled={busy !== null}
            onClick={() => void run('refresh', () => orderDeliveryClient.get(order.id, { refresh: true }))}
          >
            <RefreshCw className={`h-3.5 w-3.5 ${busy === 'refresh' ? 'animate-spin' : ''}`} />
            {t('delivery.section.refresh')}
          </Button>
        )
      }
    >
      {dispatchQuery.isLoading && !summary ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {t('delivery.section.loading')}
        </p>
      ) : !status ? (
        <p className="text-xs text-muted-foreground">{t('delivery.section.noDispatch', { provider: provider.name })}</p>
      ) : (
        <div className="space-y-3">
          {attention && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800/60 dark:bg-red-950/40"
            >
              <p className="flex items-center gap-1.5 text-sm font-medium text-red-800 dark:text-red-300">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                {t(`delivery.section.attention.${status}`, { provider: provider.name })}
              </p>
              <p className="mt-0.5 text-xs break-words text-red-700 dark:text-red-300">
                {dispatch?.errorMessage ||
                  dispatch?.cancelReason ||
                  t('delivery.section.attentionFallback', { provider: provider.name })}
                {dispatch?.cancelledBy && (
                  <> · {t(`delivery.section.cancelledBy.${dispatch.cancelledBy}`, { provider: provider.name })}</>
                )}
              </p>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-1.5">
            <DeliveryStatusChip status={status} attention={attention} className="text-[11px]" />
            <span className="text-xs text-muted-foreground">{provider.name}</span>
            {bookingNumber && (
              <span className="font-mono text-xs text-muted-foreground">
                {t('delivery.booking', { number: bookingNumber })}
              </span>
            )}
            {dispatch?.paymentMode && (
              <span className="rounded-full border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground">
                {t(`delivery.paymentModeShort.${dispatch.paymentMode}`)}
              </span>
            )}
          </div>

          {courierAt && (
            <p className="text-xs text-muted-foreground">
              {t('delivery.section.courierAt', { time: format(courierAt, 'HH:mm'), provider: provider.name })}
            </p>
          )}
          {searchAt && (
            <p className="text-xs text-muted-foreground">
              {t('delivery.section.searchAt', { time: format(searchAt, 'HH:mm') })}
            </p>
          )}

          {(driverName || driverPhone) && (
            <div className="flex items-center gap-2.5 rounded-lg border border-border px-3 py-2">
              {dispatch?.driverPhotoUrl ? (
                <img
                  src={dispatch.driverPhotoUrl}
                  alt=""
                  className="h-9 w-9 shrink-0 rounded-full object-cover"
                  loading="lazy"
                />
              ) : (
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Bike className="h-4 w-4" />
                </span>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-[11px] text-muted-foreground">{t('delivery.section.driver')}</p>
                <p className="truncate text-sm font-medium text-foreground">{driverName ?? '—'}</p>
              </div>
              {driverPhone && (
                <Button variant="outline" size="sm" className="h-8 shrink-0 gap-1.5 text-xs" asChild>
                  <a
                    href={`tel:${driverPhone}`}
                    aria-label={t('delivery.section.call', { name: driverName ?? driverPhone })}
                  >
                    <Phone className="h-3.5 w-3.5" />
                    {driverPhone}
                  </a>
                </Button>
              )}
            </div>
          )}

          {deliveryCode && <DeliveryCodeRow code={deliveryCode} hint={t('delivery.section.deliveryCodeHint')} />}

          {(trackingUrl || dispatch?.merchantUrl) && (
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
              {trackingUrl && (
                <a
                  href={trackingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  <Navigation className="h-3 w-3" />
                  {t('delivery.section.tracking')}
                </a>
              )}
              {dispatch?.merchantUrl && (
                <a
                  href={dispatch.merchantUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  {t('delivery.section.merchant', { provider: provider.name })}
                </a>
              )}
            </div>
          )}

          {dispatch?.proofOfDeliveryUrl && (
            <a href={dispatch.proofOfDeliveryUrl} target="_blank" rel="noopener noreferrer" className="block w-fit">
              <span className="mb-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                <Image className="h-3 w-3" />
                {t('delivery.section.proof')}
              </span>
              <img
                src={dispatch.proofOfDeliveryUrl}
                alt={t('delivery.section.proof')}
                className="h-24 w-24 rounded-md border border-border object-cover"
                loading="lazy"
              />
            </a>
          )}

          {events.length > 0 && (
            <div>
              <p className="mb-1 text-[11px] font-medium text-muted-foreground">{t('delivery.section.timeline')}</p>
              <ol className="space-y-1 border-l border-border pl-3">
                {events.map((event, index) => (
                  <li key={`${event.at}-${index}`} className="relative text-xs">
                    <span
                      className="absolute top-1.5 -left-[15px] h-1.5 w-1.5 rounded-full bg-muted-foreground/60"
                      aria-hidden="true"
                    />
                    <span className="text-muted-foreground tabular-nums">{format(new Date(event.at), 'HH:mm')}</span>{' '}
                    <span className="text-foreground">{t(deliveryStatusKey(event.status))}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}

      {canRequest && !hasCoordinates && (
        <p id={`order-delivery-no-coords-${order.id}`} className="mt-3 text-xs text-amber-700 dark:text-amber-300">
          {t('delivery.section.noCoordinates', { provider: provider.name })}
        </p>
      )}
      {(canRequest || active) && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {canRequest && (
            <Button
              size="sm"
              className="h-8 gap-1.5 text-xs"
              disabled={!hasCoordinates}
              aria-describedby={!hasCoordinates ? `order-delivery-no-coords-${order.id}` : undefined}
              onClick={() => setRequesting(true)}
            >
              <Bike className="h-3.5 w-3.5" />
              {status ? t('delivery.section.requestAgain') : t('delivery.section.request')}
            </Button>
          )}
          {active && (
            <Button
              variant="outline"
              size="sm"
              className="h-8 gap-1.5 border-red-200 text-xs text-red-700 hover:bg-red-50 hover:text-red-800 dark:border-red-800/60 dark:text-red-300 dark:hover:bg-red-950/40 dark:hover:text-red-200"
              disabled={busy !== null}
              onClick={() => setCancelling(true)}
            >
              <X className="h-3.5 w-3.5" />
              {t('delivery.section.cancel')}
            </Button>
          )}
        </div>
      )}

      <RequestRiderDialog
        order={requesting ? order : null}
        providerType={link?.provider}
        myFleet={link?.supportsTerminal ?? false}
        defaultPrepTimeMinutes={link?.prepTimeMinutes}
        supports={link ? supportsOf(link) : undefined}
        courierLeadMinutes={link ? localHoldLeadOf(link) : undefined}
        onOpenChange={setRequesting}
        onRequested={(next) => {
          adopt(next);
          setRequesting(false);
        }}
      />

      <CancelDeliveryDialog
        locationId={cancelling ? (order.locationId ?? null) : null}
        kind="order"
        providerType={provider.id}
        notBookedYet={dispatch?.externalBookingId === null}
        busy={busy === 'cancel'}
        onConfirm={(reason) => void handleCancel(reason)}
        onDismiss={() => setCancelling(false)}
      />
    </DetailSection>
  );
}
